import { combineEpics, ofType } from 'redux-observable';
import { Observable, of } from 'rxjs';
import {
  catchError,
  filter,
  groupBy,
  map,
  mergeMap,
  startWith,
  switchMap,
  takeUntil,
  throttleTime,
} from 'rxjs/operators';
import { requestActions } from './apiHelpers';
import { AppState } from '@reducers/rootReducer';
import { ID, ReduxAction } from '../types';
import Cadence from '@models/Cadence';
import { apiObservable } from '@support/api';

/* Epic actions */
const reconnectCadenceActions = requestActions('RECONNECT_CADENCE', ['fetch', 'create', 'delete']);

/* Component actions */
export const setReconnect = reconnectCadenceActions['create'];
export const deleteReconnect = reconnectCadenceActions['delete'];

/* Selectors */
export const reconnectCadenceSelector = (contactID: ID) => (state: AppState) =>
  state.data.reconnect.collection.items.get(String(contactID), new Cadence({ type: 'auto' }));

export const selectReconnectCadenceType = (contactID: ID) => (state: AppState) => {
  const reconnectCadence = state.data.reconnect.collection.items.get(String(contactID));
  return reconnectCadence && reconnectCadence.updated ? reconnectCadence.type : 'auto';
};

/* Epics */
export const fetchReconnectCadenceEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('SELECT_CONTACT'),
    // Ignore any actions that set the selected contact to null
    filter(({ payload }) => !!payload?.contact),
    groupBy(({ payload }) => payload.contact.id),
    mergeMap((fetchesByID) =>
      fetchesByID.pipe(
        // For a given contact, only fetch the cadence at most once every 2 minutes
        throttleTime(2 * 60 * 1000),
        switchMap(({ payload }) =>
          apiObservable('GET', `/network/contacts/${payload.contact.id}/reconnect`).pipe(
            map(({ response }) =>
              reconnectCadenceActions['fetchSucceeded']({
                cadence: response,
                contactID: payload.contact.id,
              }),
            ),
            startWith(
              reconnectCadenceActions['fetchRequested']({
                contactID: payload.contact.id,
              }),
            ),
            catchError((err) =>
              of(
                reconnectCadenceActions['fetchFailed']({
                  ...err,
                  contactID: payload.contact.id,
                }),
              ),
            ),
            // If a DELETE happens while a GET is in flight, cancel the GET
            takeUntil(action$.pipe(ofType('DELETE_RECONNECT_CADENCE'))),
          ),
        ),
      ),
    ),
  );

export const createReconnectCadenceEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('CREATE_RECONNECT_CADENCE'),
    mergeMap(({ payload }) =>
      apiObservable('POST', `/network/contacts/${payload.contactID}/reconnect/`, {
        ...payload.cadence.toJSON,
      }).pipe(
        map(({ response }) =>
          reconnectCadenceActions['createSucceeded']({
            cadence: response,
            contactID: payload.contactID,
          }),
        ),
        startWith(reconnectCadenceActions['createRequested'](payload)),
        catchError((err) =>
          of(
            reconnectCadenceActions['createFailed']({
              ...err,
              contactID: payload.contactID,
            }),
          ),
        ),
      ),
    ),
  );

export const deleteReconnectCadenceEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('DELETE_RECONNECT_CADENCE'),
    mergeMap(({ payload }) =>
      apiObservable('DELETE', `/network/contacts/${payload.contactID}/reconnect`).pipe(
        map(reconnectCadenceActions['deleteSucceeded']),
        startWith(reconnectCadenceActions['deleteRequested'](payload)),
        catchError((err) => of(reconnectCadenceActions['deleteFailed'](err))),
      ),
    ),
  );

export const reconnectEpic = combineEpics(
  fetchReconnectCadenceEpic,
  createReconnectCadenceEpic,
  deleteReconnectCadenceEpic,
);
